import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes, faFileExcel, faUpload, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { AgGridAngular } from 'ag-grid-angular';
import { CellClickedEvent, ColDef, ColGroupDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { AgGridThemeService } from '../../_services/ag-grid-theme.service';
import { AG_GRID_LOCALE_DE } from '../../_helpers/aggrid.locale.de';
import {
  ExcelChange,
  ExcelPreviewCell,
  ExcelPreviewColumn,
  ExcelPreviewResult,
  ExcelPreviewRow,
  ExcelTemplateSource,
  OperationRequirementService,
} from '../../_services/operation-requirement.service';
import { ProjectService } from '../../_services/project.service';
import { ShippingConfigService, ShippingConfigPayload, ShippingConfigDefaultPreview } from '../../_services/shipping-config.service';
import {
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
} from '../../_models/shipping-config.interface';
import { ShippingConfigFormComponent } from '../_shared/shipping-config-form/shipping-config-form.component';
import {
  ShippingConfigHeaderComponent,
  ShippingConfigHeaderParams,
} from './shipping-config-header.component';

interface ProjectOption {
  id: number;
  name: string;
}

type Phase = 'setup' | 'uploading' | 'review' | 'applying' | 'done';

export type AutoCreateCategory = 'internal' | 'external_staging' | 'external_material' | 'external_other';

/**
 * Bulk-Edit-Popup fuer Versand-/Staging-Artikelmengen ueber mehrere Aufträge.
 * Vier Phasen: Setup (Projekt + Vorlagen-Quelle waehlen, Excel exportieren) →
 * Upload (Excel zurueck hochladen) → Review (Diff in AG-Grid pruefen) →
 * Applying/Done. Wird als Dialog von TicketOperations.component eingebunden.
 *
 * Inputs:
 *   - operationIds: bereits gefiltert auf das gewaehlte (externe) Projekt
 *     plus optional alle internen Operations
 *   - projectId: optional vorgewaehlt (wenn alle ausgewaehlten externen
 *     Operations zum gleichen Projekt gehoeren); leer wenn nur interne
 *     Operations selektiert wurden — dann darf der User aus der vollen
 *     Projektliste manuell waehlen
 */
@Component({
  standalone: true,
  selector: 'hawk-operations-bulk-excel',
  imports: [CommonModule, FormsModule, FontAwesomeModule, AgGridAngular, ShippingConfigFormComponent],
  templateUrl: './operations-bulk-excel.component.html',
  // ViewEncapsulation.None, weil AG-Grid-Headerklassen ausserhalb der
  // Komponenten-Scope-Attribute gerendert werden — Tailwind ist hier nicht
  // anwendbar. Klassennamen sind explizit auf die Diff-Tabelle eingegrenzt.
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .bulk-excel-hawk-link { color: #2563eb; cursor: pointer; text-decoration: underline; }
    .bulk-excel-hawk-link:hover { color: #1d4ed8; }
    .bulk-excel-shipping-cell { cursor: pointer; }
    .bulk-excel-shipping-cell:hover { filter: brightness(0.97); }
    .bulk-excel-shipping-empty { color: #6b7280; font-style: italic; }
  `],
})
export class OperationsBulkExcelComponent implements OnInit {
  @Input() operationIds: number[] = [];
  @Input() projectId: number | null = null;
  @Output() closeDialog = new EventEmitter<void>();
  @Output() imported = new EventEmitter<void>();

  private excelService = inject(OperationRequirementService);
  private projectService = inject(ProjectService);
  private shippingConfigService = inject(ShippingConfigService);

  // Spalten-ID der Versand-Config-Cell — wird im onCellClicked-Handler
  // ausgewertet, um genau diese Spalte als Klick-Trigger fuer den Edit-
  // Dialog zu erkennen.
  private static readonly SHIPPING_CONFIG_COL_ID = 'shipping_config';

  // State fuer den Versand-Config-Edit-Dialog. Wenn gesetzt, wird der
  // Dialog mit der Form-Komponente gerendert.
  protected editingShippingRow = signal<ExcelPreviewRow | null>(null);
  protected editingShippingContext = signal<ShippingConfigContext | null>(null);

  // Pending Versand-Config-Aenderungen pro Op-ID. Diese werden NICHT direkt
  // beim Speichern im Form persistiert, sondern erst beim Apply-Klick zusammen
  // mit den Mengen-Aenderungen — analog zum Diff-Workflow fuer Artikel-Mengen.
  protected pendingShippingConfigs = signal<Map<number, ShippingConfigFormData>>(new Map());

  // Auto-Create-Modal-State (Standard-Configs fuer alle fehlenden anlegen).
  protected showAutoCreateModal = signal<boolean>(false);
  // Default-Vorlaufzeiten je Auftragskategorie. Material-Versand-Priority und
  // interne Auftraege brauchen keine Vorlaufzeit (sofortiger Versand); externe
  // Standard-Auftraege ~4 Wochen, Externe mit Staging-Artikel ebenfalls 28
  // Tage Vorlauf bis zum Vor-Ort-Einsatz.
  protected leadTimeInternalDays = signal<number>(0);
  protected leadTimeExternalMaterialDays = signal<number>(0);
  protected leadTimeExternalStagingDays = signal<number>(28);
  protected leadTimeExternalOtherDays = signal<number>(28);
  protected autoCreateRunning = signal<boolean>(false);
  protected autoCreateError = signal<string | null>(null);

  // GridApi-Ref fuer manuelle Cell-Refreshes. AG-Grid kennt die Pending-Map
  // nicht (kein rowData-Feld), daher muessen wir die Versandkonfig-Spalte
  // explizit invalidieren, sobald sich die Pending-Map aendert.
  private gridApi: GridApi<ExcelPreviewRow> | null = null;

  constructor() {
    effect(() => {
      // Beide Signals lesen, damit der Effekt auf jede Aenderung triggert,
      // die die Versandkonfig-Cell oder den Header-Counter beeinflusst.
      this.pendingShippingConfigs();
      this.rowsMissingShippingConfig();
      this.gridApi?.refreshCells({
        columns: [OperationsBulkExcelComponent.SHIPPING_CONFIG_COL_ID],
        force: true,
      });
      this.gridApi?.refreshHeader();
    });
  }

  protected onGridReady = (event: GridReadyEvent<ExcelPreviewRow>): void => {
    this.gridApi = event.api;
  };

  protected gridTheme = inject(AgGridThemeService).getTheme();
  protected gridLocale = AG_GRID_LOCALE_DE;

  readonly faTimes = faTimes;
  readonly faExcel = faFileExcel;
  readonly faUpload = faUpload;
  readonly faCheck = faCheck;
  readonly faWarn = faExclamationTriangle;

  protected phase = signal<Phase>('setup');
  protected selectedProjectId = signal<number | null>(null);
  protected templateSource = signal<ExcelTemplateSource>('project');
  protected projects = signal<ProjectOption[]>([]);
  protected uploadedFile = signal<File | null>(null);
  protected previewResult = signal<ExcelPreviewResult | null>(null);
  protected applyResult = signal<{ created: number; updated: number; deleted: number; errors: string[] } | null>(null);
  protected errorMessage = signal<string | null>(null);

  protected validChanges = computed<ExcelChange[]>(() => {
    const result = this.previewResult();
    return result ? result.changes.filter((c) => !c.error) : [];
  });

  protected invalidChanges = computed<ExcelChange[]>(() => {
    const result = this.previewResult();
    return result ? result.changes.filter((c) => !!c.error) : [];
  });

  // Wide-Format-Diff: pro Operation eine Zeile, pro Template/Freitext eine
  // Spalte; Diff-Werte werden als +X/-X/'-' angezeigt. Artikelspalten werden
  // in drei Gruppen gerendert: Staging-Artikel (blau), Versand-Artikel
  // (neutral), Mitgeb-Artikel (orange — entspricht Freitext-Spalten).
  protected previewColumnDefs = computed<(ColDef<ExcelPreviewRow> | ColGroupDef<ExcelPreviewRow>)[]>(() => {
    const result = this.previewResult();
    if (!result) return [];

    // Pinned-Info-Spalten: bei geaenderten Zeilen mit gruenem Hintergrund
    // markieren — gleiche Farblogik wie die Diff-Cells, damit "geaendert"
    // konsistent gruen ist (blau ist fuer Staging-Artikel reserviert).
    const pinnedStyle = (p: { data?: ExcelPreviewRow }) =>
      p.data?.has_change ? { backgroundColor: 'rgba(34, 197, 94, 0.18)' } : undefined;

    const cols: (ColDef<ExcelPreviewRow> | ColGroupDef<ExcelPreviewRow>)[] = [
      {
        field: 'order_nr',
        headerName: 'Hawk-Nr',
        pinned: 'left',
        width: 130,
        cellStyle: pinnedStyle,
        // Klick auf Hawk-Nr oeffnet die Ticket-Page mit der konkreten
        // Operation in einem neuen Tab. operation_id wird als Query-Param
        // gelesen (siehe ticket-operations.component.ts:167).
        cellRenderer: (p: { data?: ExcelPreviewRow }) => {
          const row = p.data;
          if (!row || !row.ticket_id || !row.order_nr) return row?.order_nr ?? '';
          return `<a class="bulk-excel-hawk-link" href="/a/ticket/${row.ticket_id}?operation_id=${row.operation_id}" target="_blank" rel="noopener">${row.order_nr}</a>`;
        },
      },
      { field: 'operation_id', headerName: 'Einsatz ID', pinned: 'left', width: 110, cellStyle: pinnedStyle },
    ];

    const stagingCols: ColDef<ExcelPreviewRow>[] = [];
    const versandCols: ColDef<ExcelPreviewRow>[] = [];
    const mitgebCols: ColDef<ExcelPreviewRow>[] = [];

    for (const col of result.preview_columns) {
      const isStaging = col.kind === 'template' && col.is_staging;
      const isMitgeb = col.kind === 'free_text';

      const colDef: ColDef<ExcelPreviewRow> = {
        headerName: col.label,
        colId: col.id,
        minWidth: 140,
        flex: 1,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        valueGetter: (p) => {
          const cell = p.data?.cells[col.id];
          return cell ? cell.new_quantity : 0;
        },
        cellRenderer: (p: { data?: ExcelPreviewRow }) => {
          const cell = p.data?.cells[col.id];
          if (!cell || (cell.old_quantity === 0 && cell.new_quantity === 0)) {
            return '<span class="text-base-content/30">-</span>';
          }
          if (!cell.has_change) {
            return `${cell.new_quantity}`;
          }
          if (cell.is_parsing_takeover) {
            return `${cell.new_quantity} <span class="text-info font-bold" title="Aus Auftragsbeschreibung übernommen">↻</span>`;
          }
          const sign = cell.delta > 0 ? '+' : '';
          const cls = cell.has_error
            ? 'text-error font-bold'
            : cell.delta > 0
              ? 'text-success font-bold'
              : 'text-warning font-bold';
          return `${cell.new_quantity} <span class="${cls}">(${sign}${cell.delta})</span>`;
        },
        // Diff-Status faerbt die Cell — kategorische Spaltenfaerbung (Staging/
        // Mitgeb) wird in der AG-Grid-Tabelle bewusst nicht angewendet, damit
        // die Tabelle ruhiger wirkt. In der Excel bleibt die Faerbung aktiv.
        cellStyle: (p) => {
          const cell = p.data?.cells[col.id];
          if (!cell) return undefined;
          if (cell.has_error) return { backgroundColor: 'rgba(239, 68, 68, 0.15)' };
          if (cell.is_parsing_takeover) return { backgroundColor: 'rgba(59, 130, 246, 0.10)' };
          if (cell.has_change) return { backgroundColor: 'rgba(34, 197, 94, 0.08)' };
          return undefined;
        },
        tooltipValueGetter: (p) => {
          const cell = p.data?.cells[col.id];
          if (!cell) return '';
          if (cell.error) return cell.error;
          if (cell.is_parsing_takeover) return 'Aus Auftragsbeschreibung übernommen — wird nach Import als Versand-Artikel im Artikelbedarf-Tab verfügbar';
          if (!cell.has_change) return '';
          return `${cell.action_type}: alt ${cell.old_quantity} → neu ${cell.new_quantity}`;
        },
      };

      if (isStaging) stagingCols.push(colDef);
      else if (isMitgeb) mitgebCols.push(colDef);
      else versandCols.push(colDef);
    }

    if (stagingCols.length) {
      cols.push({ headerName: 'Staging-Artikel', children: stagingCols });
    }
    if (versandCols.length) {
      cols.push({ headerName: 'Versand-Artikel', children: versandCols });
    }
    if (mitgebCols.length) {
      cols.push({ headerName: 'Mitgeb-Artikel', children: mitgebCols });
    }

    // Rechts gepinnt: aktuelle Versandkonfiguration pro Auftrag — Klick
    // oeffnet den Edit-/Create-Dialog mit der geteilten Form-Komponente.
    cols.push({
      colId: OperationsBulkExcelComponent.SHIPPING_CONFIG_COL_ID,
      headerName: 'Versandkonfiguration',
      headerComponent: ShippingConfigHeaderComponent,
      headerComponentParams: {
        missingCount: () => this.rowsMissingShippingConfig().size,
        onAutoCreate: () => this.openAutoCreateModal(),
      } as Partial<ShippingConfigHeaderParams>,
      autoHeaderHeight: true,
      pinned: 'right',
      width: 240,
      sortable: false,
      // Pointer-Cursor nur fuer klickbare Cells (Auftraege ohne Mengen und
      // ohne bestehende/pending Config sind nicht klickbar — keine Config noetig).
      cellClass: (p: { data?: ExcelPreviewRow }) =>
        this.isShippingCellClickable(p.data) ? 'bulk-excel-shipping-cell' : '',
      cellRenderer: (p: { data?: ExcelPreviewRow }) => this.renderShippingConfigCell(p.data),
      // Pending-Status faerbt die Cell ein: gruen fuer neue Configs,
      // blau fuer aenderungen, ROT fuer fehlende Pflicht-Configs (Auftrag hat
      // Versandartikel, aber keine Config geplant — Apply blockiert).
      cellStyle: (p: { data?: ExcelPreviewRow }) => {
        const row = p.data;
        if (!row) return undefined;
        if (this.rowsMissingShippingConfig().has(row.operation_id)) {
          return { backgroundColor: 'rgba(239, 68, 68, 0.18)' };
        }
        const pending = this.pendingShippingConfigs().get(row.operation_id);
        if (!pending) return undefined;
        const isCreate = !row.shipping_config;
        return isCreate
          ? { backgroundColor: 'rgba(34, 197, 94, 0.15)' }
          : { backgroundColor: 'rgba(59, 130, 246, 0.15)' };
      },
      tooltipValueGetter: (p) => {
        const row = p.data;
        if (!row) return '';
        if (this.rowsMissingShippingConfig().has(row.operation_id)) {
          return 'Versandkonfiguration fehlt — Auftrag hat Versandartikel, aber keine Config. Import wird blockiert, bis alle markierten Aufträge eine Config haben.';
        }
        const pending = this.pendingShippingConfigs().get(row.operation_id);
        if (pending) {
          const prefix = row.shipping_config ? 'Geänderte Versandkonfiguration (noch nicht importiert)' : 'Neue Versandkonfiguration (noch nicht importiert)';
          return `${prefix} — Klicken zum Anpassen`;
        }
        if (row.shipping_config) return this.formatShippingConfigTooltip(row.shipping_config);
        if (!this.rowHasArticles(row)) return 'Auftrag hat keine Versandartikel — keine Versandkonfiguration nötig.';
        return 'Klicken, um eine Versandkonfiguration anzulegen';
      },
    });

    return cols;
  });

  // Kompakte Anzeige der Versandkonfiguration — drei States:
  //   1. Pending-Create (gruen ⊕) — pending Config, vorher keine vorhanden
  //   2. Pending-Update (orange ✎) — pending Config, ueberschreibt bestehende
  //   3. Unchanged — bestehende Config, kein Diff
  //   4. Empty — keine Config, nichts geplant → Anlegen-Hint
  private renderShippingConfigCell(row: ExcelPreviewRow | undefined): string {
    if (!row) return '';
    const pending = this.pendingShippingConfigs().get(row.operation_id);
    const sc = row.shipping_config;

    if (pending) {
      const isCreate = !sc;
      const icon = isCreate ? '⊕' : '✎';
      const colorClass = isCreate ? 'text-success' : 'text-info';
      const label = this.formDataLabel(pending);
      const method = this.formDataMethodLabel(pending);
      const suffix = method ? ` (${method})` : '';
      return `<span class="${colorClass} font-semibold text-xs">${icon} ${this.escapeHtml(label)}${this.escapeHtml(suffix)}</span>`;
    }

    if (!sc) {
      if (this.rowsMissingShippingConfig().has(row.operation_id)) {
        return '<span class="text-error font-semibold text-xs">⚠ Versandkonfig anlegen</span>';
      }
      // Kein Artikelbedarf → keine Config noetig, Klick deaktiviert.
      if (!this.rowHasArticles(row)) {
        return '<span class="bulk-excel-shipping-empty">nicht benötigt</span>';
      }
      return '<span class="bulk-excel-shipping-empty">+ Versandkonfiguration anlegen</span>';
    }
    const label = sc.shipping_type_label || sc.shipping_type;
    const method = this.shippingMethodShortLabel(sc);
    const suffix = method ? ` (${method})` : '';
    return `<span class="text-xs">${this.escapeHtml(label)}${this.escapeHtml(suffix)}</span>`;
  }

  // Best-effort Label fuer pending FormData — kennt anders als shipping_type_label
  // den Stations-/Filialnamen nicht (das Form-Datum hat nur die ID), daher hier
  // bewusst kuerzer als bei der bestehenden Config.
  private formDataLabel(form: ShippingConfigFormData): string {
    switch (form.shipping_type) {
      case 'pickpoint': return 'PickPoint';
      case 'store_shipping': return form.article_disposition === 'consumed' ? 'Filialversand (verbraucht)' : 'Filialversand';
      case 'technician_shipping': return 'Technikerversand';
      case 'customer_shipping': return 'Kundenversand';
      default: return form.shipping_type;
    }
  }

  private formDataMethodLabel(form: ShippingConfigFormData): string {
    const map: Record<string, string> = { parcel: 'Paket', pallet: 'Palette', courier: 'Kurier', pickup: 'Abholung' };
    const m = map[form.shipment_method] ?? '';
    if (!m) return '';
    if (form.shipment_method === 'parcel' && form.package_speed === 'express') return `${m} Express`;
    return m;
  }

  private shippingMethodShortLabel(sc: ShippingConfig): string {
    const map: Record<string, string> = { parcel: 'Paket', pallet: 'Palette', courier: 'Kurier', pickup: 'Abholung' };
    const m = sc.shipment_method ? map[sc.shipment_method] : '';
    if (!m) return '';
    if (sc.shipment_method === 'parcel' && sc.package_speed === 'express') return `${m} Express`;
    return m;
  }

  private formatShippingConfigTooltip(sc: ShippingConfig): string {
    const parts: string[] = [sc.shipping_type_label || sc.shipping_type];
    const method = this.shippingMethodShortLabel(sc);
    if (method) parts.push(method);
    if (sc.target_user_name) parts.push(`Ziel: ${sc.target_user_name}`);
    if (sc.shipping_date) parts.push(`Versand: ${sc.shipping_date}`);
    return parts.join(' · ');
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }

  // Hat die Operation nach dem Apply mindestens einen Versandartikel-Eintrag?
  // Basis fuer "Config noetig?"-Entscheidung — Aufträge ohne Artikel
  // brauchen keine Config (Klick auf die Cell ist deaktiviert).
  private rowHasArticles(row: ExcelPreviewRow): boolean {
    return Object.values(row.cells).some((c) => c.new_quantity > 0);
  }

  // Cell ist klickbar, wenn entweder eine Config existiert/geplant ist
  // oder der Auftrag ueberhaupt Versandartikel hat.
  private isShippingCellClickable(row: ExcelPreviewRow | undefined): boolean {
    if (!row) return false;
    if (row.shipping_config) return true;
    if (this.pendingShippingConfigs().has(row.operation_id)) return true;
    return this.rowHasArticles(row);
  }

  protected onCellClicked = (event: CellClickedEvent<ExcelPreviewRow>): void => {
    if (event.column.getColId() !== OperationsBulkExcelComponent.SHIPPING_CONFIG_COL_ID) return;
    const row = event.data;
    if (!row) return;
    // Aufträge ohne Versandartikel benoetigen keine Config — Klick blockiert.
    if (!this.isShippingCellClickable(row)) return;
    this.openShippingConfigDialog(row);
  };

  // Oeffnet den Versand-Config-Edit-Dialog: setzt die zu bearbeitende Zeile
  // und laedt den Buero-Kontext nach (Rueckadresse-Flag fuer Kundenversand —
  // ist nicht in der Excel-Preview enthalten und wird nur on-demand gebraucht).
  private async openShippingConfigDialog(row: ExcelPreviewRow): Promise<void> {
    this.editingShippingRow.set(row);
    this.editingShippingContext.set(null);
    try {
      const ctx = await this.shippingConfigService.getContext(row.operation_id);
      this.editingShippingContext.set(ctx);
    } catch (e) {
      console.error('Versand-Kontext konnte nicht geladen werden', e);
      this.editingShippingContext.set({ customer_has_return_address: false });
    }
  }

  protected closeShippingConfigDialog(): void {
    this.editingShippingRow.set(null);
    this.editingShippingContext.set(null);
  }

  // Wird vom Custom-Header-Button getriggert: oeffnet das Modal mit der
  // Vorlaufzeit-Tabelle. Nur klickbar, wenn es ueberhaupt fehlende Configs
  // gibt (Header-Button ist sonst disabled).
  protected openAutoCreateModal(): void {
    if (this.rowsMissingShippingConfig().size === 0) return;
    this.autoCreateError.set(null);
    this.showAutoCreateModal.set(true);
  }

  protected closeAutoCreateModal(): void {
    if (this.autoCreateRunning()) return;
    this.showAutoCreateModal.set(false);
  }

  // Holt vom Backend pro fehlende Op die berechnete Default-Config, packt
  // sie als pending in die Map und schliesst das Modal. Persistiert wird
  // erst beim Apply-Klick (zusammen mit Mengen-Diff).
  protected async applyAutoCreate(): Promise<void> {
    const missingIds = Array.from(this.rowsMissingShippingConfig());
    if (missingIds.length === 0) return;

    this.autoCreateRunning.set(true);
    this.autoCreateError.set(null);
    try {
      // Staging-Op-IDs aus der Frontend-Klassifizierung (categorizeRow) als
      // Hint mitschicken: das Backend kann sonst nicht erkennen, dass diese
      // Auftraege nach dem Excel-Import STAGING-Requirements haben werden,
      // und wuerde sie faelschlich als external_other (Filialversand) statt
      // external_staging (Technikerversand) klassifizieren.
      const pendingStagingOpIds = this.categorizedMissingRows().external_staging.map((r) => r.operation_id);
      const previews = await this.shippingConfigService.previewDefaults(missingIds, {
        internal: this.leadTimeInternalDays(),
        external_staging: this.leadTimeExternalStagingDays(),
        external_material: this.leadTimeExternalMaterialDays(),
        external_other: this.leadTimeExternalOtherDays(),
      }, pendingStagingOpIds);
      const map = new Map(this.pendingShippingConfigs());
      for (const preview of previews) {
        map.set(preview.operation_id, this.previewToFormData(preview));
      }
      this.pendingShippingConfigs.set(map);
      this.showAutoCreateModal.set(false);
    } catch (e) {
      console.error('Standard-Configs konnten nicht berechnet werden', e);
      this.autoCreateError.set('Backend-Aufruf fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      this.autoCreateRunning.set(false);
    }
  }

  // Backend-Preview-Struktur → ShippingConfigFormData fuer pendingShippingConfigs.
  // article_disposition wird auf einen Default zurueckgesetzt, falls das Backend
  // null liefert (z.B. legacy-Configs ohne explizite Disposition).
  private previewToFormData(preview: ShippingConfigDefaultPreview): ShippingConfigFormData {
    const f = preview.form_data;
    return {
      shipping_type: f.shipping_type,
      shipment_method: f.shipment_method,
      package_speed: f.package_speed ?? null,
      article_disposition: f.article_disposition ?? 'technician_transfer',
      shipping_date: f.shipping_date ?? null,
      pickpoint_station_id: f.pickpoint_station_id ?? null,
      target_user_id: f.target_user_id ?? null,
      target_warehouse_id: f.target_warehouse_id ?? null,
      notes: f.notes ?? undefined,
      pm_code: f.pm_code ?? null,
    };
  }

  // initialConfig fuer den Form-Dialog: bevorzugt pending FormData (sodass
  // der User seine bisherigen Eingaben beim Re-Open weitersieht), fallback
  // auf die bestehende Backend-Config der Zeile.
  protected editingInitialConfig = computed<ShippingConfig | null>(() => {
    const row = this.editingShippingRow();
    if (!row) return null;
    const pending = this.pendingShippingConfigs().get(row.operation_id);
    if (pending) return this.formDataAsConfig(pending, row.shipping_config ?? null);
    return row.shipping_config ?? null;
  });

  // Form-Save schickt jetzt KEINEN Backend-Request mehr; die Aenderung wird
  // als pending-Eintrag gespeichert und erst beim Apply persistiert.
  protected onShippingConfigSave(data: ShippingConfigFormData): void {
    const row = this.editingShippingRow();
    if (!row) return;
    const map = new Map(this.pendingShippingConfigs());
    map.set(row.operation_id, data);
    this.pendingShippingConfigs.set(map);
    this.closeShippingConfigDialog();
  }

  // FormData → Pseudo-ShippingConfig fuer Re-Edit-Anzeige. PickPoint-Station-
  // Object und target_user_name bleiben nur erhalten, wenn die IDs unveraendert
  // sind — sonst muss der User die Werte beim Re-Open neu auswaehlen.
  private formDataAsConfig(form: ShippingConfigFormData, base: ShippingConfig | null): ShippingConfig {
    return {
      id: base?.id ?? 0,
      operation_id: base?.operation_id ?? 0,
      shipping_type: form.shipping_type,
      shipping_type_label: base?.shipping_type_label,
      shipment_method: form.shipment_method,
      package_speed: form.package_speed ?? undefined,
      article_disposition: form.article_disposition,
      shipping_date: form.shipping_date ?? undefined,
      pickpoint_station_id: form.pickpoint_station_id ?? undefined,
      pickpoint_station: form.pickpoint_station_id === base?.pickpoint_station_id ? base?.pickpoint_station : undefined,
      target_user_id: form.target_user_id ?? undefined,
      target_user_name: form.target_user_id === base?.target_user_id ? base?.target_user_name : undefined,
      target_warehouse_id: form.target_warehouse_id ?? undefined,
      target_warehouse: form.target_warehouse_id === base?.target_warehouse_id ? base?.target_warehouse : undefined,
      notes: form.notes,
      pm_code: form.pm_code ?? undefined,
      pickup_date: form.pickup_date ?? null,
    };
  }

  protected previewDefaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
  };

  protected getPreviewRowId = (params: { data: ExcelPreviewRow }) => String(params.data.operation_id);

  protected changedRowsCount = computed<number>(() => {
    const result = this.previewResult();
    return result ? result.preview_rows.filter((r) => r.has_change).length : 0;
  });

  protected hasParsingTakeovers = computed<boolean>(() => {
    const result = this.previewResult();
    if (!result) return false;
    return result.preview_rows.some((r) =>
      Object.values(r.cells).some((c) => c.is_parsing_takeover)
    );
  });

  protected totalRowsCount = computed<number>(() => {
    const result = this.previewResult();
    return result ? result.preview_rows.length : 0;
  });

  // Schwelle, ab der das Grid intern scrollt statt sich an die Daten anzupassen.
  private readonly MAX_VISIBLE_ROWS = 10;
  // Reserve fuer Group-Header (Staging/Versand/Mitgeb) + mehrzeilige
  // Spaltenheader bei langen Vorlagen-Namen.
  private readonly HEADER_PX = 170;
  private readonly ROW_PX = 36;

  // Bei wenigen Zeilen: autoHeight → Grid wird exakt so hoch wie noetig, kein
  // leerer Raum unter den Daten. Ab MAX_VISIBLE_ROWS: normal-Layout mit fester
  // Hoehe fuer 10 Zeilen + Header → internes vertikales Scrollen, fixierter
  // Header.
  protected gridDomLayout = computed<'normal' | 'autoHeight'>(() => {
    return this.totalRowsCount() <= this.MAX_VISIBLE_ROWS ? 'autoHeight' : 'normal';
  });

  protected gridHeightStyle = computed<string>(() => {
    if (this.gridDomLayout() === 'autoHeight') return 'auto';
    const padding = 8;
    return `${this.HEADER_PX + this.MAX_VISIBLE_ROWS * this.ROW_PX + padding}px`;
  });

  ngOnInit(): void {
    this.selectedProjectId.set(this.projectId);
    this.loadProjects();
  }

  // Aktive Projekte laden (ohne interne) — der User soll auch dann ein
  // Projekt waehlen koennen, wenn die Selektion rein interne Auftraege
  // enthaelt. Archivierte Projekte werden ausgeblendet, um die Liste
  // ueberschaubar zu halten.
  private loadProjects(): void {
    this.projectService.getActive().subscribe({
      next: (active) => {
        const filtered = active
          .filter((p) => !(p.name || '').startsWith('[INTERN]'))
          .map((p) => ({ id: p.id, name: p.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.projects.set(filtered);
      },
      error: (err) => {
        console.error('Projekte konnten nicht geladen werden', err);
        this.errorMessage.set('Projektliste konnte nicht geladen werden.');
      },
    });
  }

  protected close(): void {
    this.closeDialog.emit();
  }

  protected canExport(): boolean {
    return this.selectedProjectId() != null && this.operationIds.length > 0;
  }

  protected async onExport(): Promise<void> {
    const projectId = this.selectedProjectId();
    if (projectId == null) return;

    this.errorMessage.set(null);
    try {
      const blob = await this.excelService.exportExcel(this.operationIds, projectId, this.templateSource());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Versand_Bulk_${projectId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      this.phase.set('uploading');
    } catch (err) {
      console.error('Excel-Export fehlgeschlagen', err);
      this.errorMessage.set('Export fehlgeschlagen — bitte erneut versuchen.');
    }
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    this.uploadedFile.set(file);
  }

  protected async onUpload(): Promise<void> {
    const file = this.uploadedFile();
    if (!file) return;

    this.errorMessage.set(null);
    this.previewResult.set(null);
    try {
      const result = await this.excelService.importExcelPreview(file);
      this.previewResult.set(result);
      this.phase.set('review');
    } catch (err) {
      console.error('Excel-Preview fehlgeschlagen', err);
      this.errorMessage.set('Datei konnte nicht gelesen werden.');
    }
  }

  protected backToUpload(): void {
    this.phase.set('uploading');
    this.previewResult.set(null);
  }

  // Anzahl pending Versand-Config-Aenderungen — wird zum Mengen-Counter
  // addiert, damit der Apply-Button beide Diff-Quellen widerspiegelt.
  protected pendingShippingConfigsCount = computed<number>(() => this.pendingShippingConfigs().size);

  protected totalApplyCount = computed<number>(() => this.validChanges().length + this.pendingShippingConfigsCount());

  // Aufträge, die nach dem Apply Versandartikel haben werden, aber weder
  // eine bestehende noch eine pending Versandkonfig — Apply blockiert, bis
  // alle eine Config bekommen. "Versandartikel" = irgendeine Cell mit
  // new_quantity > 0 (egal ob Versand-, Staging- oder Mitgeb-Spalte).
  protected rowsMissingShippingConfig = computed<Set<number>>(() => {
    const result = this.previewResult();
    if (!result) return new Set<number>();
    const pending = this.pendingShippingConfigs();
    const ids = new Set<number>();
    for (const row of result.preview_rows) {
      if (!row.has_change) continue;
      if (row.shipping_config) continue;
      if (pending.has(row.operation_id)) continue;
      const hasArticles = Object.values(row.cells).some((c) => c.new_quantity > 0);
      if (hasArticles) ids.add(row.operation_id);
    }
    return ids;
  });

  protected canApply = computed<boolean>(() =>
    this.totalApplyCount() > 0 && this.rowsMissingShippingConfig().size === 0,
  );

  // Klassifizierung der fehlenden Aufträge in 4 Auftragskategorien — Basis
  // fuer die Tabelle im Auto-Create-Modal (Counts, Hawk-Nummern, Lead-Time-
  // Inputs werden pro Kategorie aktiviert/deaktiviert). Reihenfolge der
  // Checks spiegelt die Backend-Logik in OperationShippingConfigCreator:
  // intern hat Vorrang, dann Staging, dann Material-Priority, sonst other.
  protected categorizedMissingRows = computed<Record<AutoCreateCategory, ExcelPreviewRow[]>>(() => {
    const buckets: Record<AutoCreateCategory, ExcelPreviewRow[]> = {
      internal: [],
      external_staging: [],
      external_material: [],
      external_other: [],
    };
    const result = this.previewResult();
    if (!result) return buckets;
    const missing = this.rowsMissingShippingConfig();
    for (const row of result.preview_rows) {
      if (!missing.has(row.operation_id)) continue;
      buckets[this.categorizeRow(row)].push(row);
    }
    return buckets;
  });

  private categorizeRow(row: ExcelPreviewRow): AutoCreateCategory {
    if (row.is_internal) return 'internal';
    const hasStaging = Object.values(row.cells).some(
      (c) => c.action_type === 'STAGING' && c.new_quantity > 0,
    );
    if (hasStaging) return 'external_staging';
    if (row.priority_material_shipping) return 'external_material';
    return 'external_other';
  }

  protected async onApply(): Promise<void> {
    const valid = this.validChanges();
    const pendingConfigs = this.pendingShippingConfigs();
    if (valid.length === 0 && pendingConfigs.size === 0) return;

    this.phase.set('applying');
    this.errorMessage.set(null);
    const configErrors: string[] = [];
    let configCreated = 0;
    let configUpdated = 0;
    try {
      // 1. Pending Versandkonfigurationen sequenziell persistieren. Existierte
      //    vorher noch keine Config → "neu" gezaehlt, sonst → "aktualisiert".
      const previewRowsById = new Map(
        (this.previewResult()?.preview_rows ?? []).map((r) => [r.operation_id, r])
      );
      for (const [opId, formData] of pendingConfigs) {
        try {
          const existed = !!previewRowsById.get(opId)?.shipping_config;
          await this.shippingConfigService.createOrUpdateShippingConfig(
            opId,
            formData as unknown as ShippingConfigPayload
          );
          if (existed) configUpdated++;
          else configCreated++;
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
          configErrors.push(`Op ${opId}: ${msg}`);
        }
      }

      // 2. Mengen-Diff anwenden (kann leer sein, wenn nur Configs geaendert wurden).
      const mengen = valid.length > 0
        ? await this.excelService.importExcelApply(valid)
        : { success: true, created: 0, updated: 0, deleted: 0, errors: [] };

      const allErrors = [...mengen.errors, ...configErrors];
      this.applyResult.set({
        created: mengen.created + configCreated,
        updated: mengen.updated + configUpdated,
        deleted: mengen.deleted,
        errors: allErrors,
      });
      this.phase.set('done');
      if (mengen.success && configErrors.length === 0) {
        this.imported.emit();
        this.pendingShippingConfigs.set(new Map());
      }
    } catch (err) {
      console.error('Excel-Apply fehlgeschlagen', err);
      this.errorMessage.set('Import fehlgeschlagen.');
      this.phase.set('review');
    }
  }

  protected changeRowClass(change: ExcelChange): string {
    if (change.error) return 'bg-error/20';
    if (change.kind === 'create') return 'bg-success/10';
    if (change.kind === 'delete') return 'bg-warning/10';
    return 'bg-info/10';
  }

  protected changeKindLabel(kind: ExcelChange['kind']): string {
    switch (kind) {
      case 'create': return 'Neu';
      case 'update': return 'Geändert';
      case 'delete': return 'Gelöscht';
    }
  }
}
